import { useEffect, useRef } from "react";
import { Heart, Shield, Lightbulb, Footprints, Camera } from "lucide-react";

const JR_IMAGE = "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/xd7alt8t_Screenshot%202026-05-10%20at%2010.03.58%E2%80%AFAM.png";
const JAY_SOCCER = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/029iym6q_rs%3Dw_720%2Ch_720%2Ccg_true.webp";
const JR_CLASSIC = "https://customer-assets.emergentagent.com/job_tyss-redesigned/artifacts/2ubufy9k_rs%3Dw_730%2Ch_730%2Ccg_true-1.webp";
const JR_CLOSEUP = "https://customer-assets.emergentagent.com/job_trauma-youth-digital/artifacts/txirv97p_3.jpg";

const JR_GALLERY = [
  { src: JR_IMAGE, alt: "J.R. with headphones — finding flow through music" },
  { src: JR_CLOSEUP, alt: "J.R. up close — peer support specialist at TYSS" },
  { src: JAY_SOCCER, alt: "J.R. on the soccer field — sports as a coping outlet" },
  { src: JR_CLASSIC, alt: "J.R. — original portrait, the catalyst behind TYSS" },
];

const BLOG_POSTS = [
  { icon: Footprints, title: "Soccer, Stigma, and Finding My Way", excerpt: "How sports became an outlet for managing mental health challenges and breaking free from the stigma that comes with it. A personal story of resilience on and off the field." },
  { icon: Shield, title: "Surviving the Holidays", excerpt: "The holidays can be tough when you're navigating mental health challenges. Tips and personal reflections on getting through the season with grace and self-compassion." },
  { icon: Lightbulb, title: "Radical Acceptance", excerpt: "Learning to accept what we can't change — and finding power in that acceptance. A journey through one of the most transformative coping skills in recovery." },
  { icon: Heart, title: "Coping Strategies That Work for Me", excerpt: "Real, practical tools from lived experience. From grounding techniques to creative outlets — what actually helps when the world feels overwhelming." },
];

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

export default function JaysCorner() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <main ref={sectionRef} className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A3A4A] to-[#1A3A4A]/90 text-white relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#3DAA35]/15 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#7CC8E0] mb-4">
                <span className="inline-block w-10 h-[2px] bg-[#3DAA35] mr-3 align-middle" />Peer Support & Healing
              </p>
              <h1 data-testid="jays-corner-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
                Jay's <span className="text-[#7CC8E0]">Corner</span>
              </h1>
              <p className="text-base sm:text-lg font-sans font-medium text-white/80 leading-relaxed">
                A safe, nurturing space where youth share personal tips, tools, and real stories about mental health recovery. Written by J.R. — Peer Support Specialist, inspired by lived experience navigating mental health challenges.
              </p>
            </div>
            <div className="animate-on-scroll delay-200 flex justify-center gap-4">
              <img src={JR_IMAGE} alt="J.R. - Peer Support Specialist at TYSS" className="w-48 lg:w-56 h-auto rounded-2xl shadow-2xl object-contain" style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.3))" }} />
              <img src={JAY_SOCCER} alt="J.R. on the soccer field" className="w-36 lg:w-44 h-auto rounded-2xl shadow-2xl hidden sm:block mt-8 object-contain" style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.3))" }} />
            </div>
          </div>
        </div>
      </section>

      {/* J.R. Bio */}
      <section className="py-12 bg-[#E8F5E1]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
          <div className="w-20 h-20 rounded-full overflow-hidden shadow-md flex-shrink-0 bg-white">
            <img src={JR_CLOSEUP} alt="J.R." className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
          </div>
          <div>
            <p className="text-lg font-serif font-bold text-earth">J.R. — Peer Support Specialist</p>
            <p className="text-sm font-sans text-earth-muted">Sharing real recovery tools and insights from lived experience to help peers navigate mental health challenges, stigma, and tough times.</p>
          </div>
        </div>
      </section>

      {/* Photo Gallery — full uncropped images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-on-scroll text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0E8DB5]/10 border border-[#0E8DB5]/20 mb-4">
              <Camera className="w-4 h-4 text-[#0E8DB5]" />
              <span className="text-xs font-sans font-semibold tracking-wider uppercase text-[#0E8DB5]">Photo Gallery</span>
            </div>
            <h2 data-testid="jays-gallery-heading" className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-earth mb-3">
              Moments with <span className="gradient-text">J.R.</span>
            </h2>
            <p className="text-base font-sans text-earth-muted max-w-2xl mx-auto leading-relaxed">
              A glimpse into J.R.'s journey — on the field, behind the music, and in the moments that shape his peer support work.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {JR_GALLERY.map((photo, i) => (
              <div
                key={i}
                data-testid={`jays-gallery-photo-${i}`}
                className={`animate-on-scroll delay-${(i % 4 + 1) * 100} group rounded-2xl overflow-hidden bg-[#F8FAFB] border border-[#C8DDD8]/50 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
              >
                <div className="aspect-[3/4] w-full bg-[#1A3A4A]/5 flex items-center justify-center overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Jay's Corner */}
      <section className="py-20 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-on-scroll max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-earth mb-4">
              Real Stories. <span className="gradient-text">Real Recovery.</span>
            </h2>
            <p className="text-base sm:text-lg font-sans font-medium text-earth-muted leading-relaxed">
              Jay's Corner is a peer support and healing blog where youth share their recovery toolbox — real insights from real experiences to help peers navigate challenges like stigma, mental health crises, and tough times.
            </p>
          </div>

          {/* Blog Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {BLOG_POSTS.map((post, i) => (
              <div key={post.title} data-testid={`jays-post-${i}`} className={`animate-on-scroll delay-${(i+1)*100} p-8 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group`}>
                <div className="w-12 h-12 rounded-xl bg-[#0E8DB5]/10 flex items-center justify-center mb-5 group-hover:bg-[#0E8DB5] transition-colors duration-300">
                  <post.icon className="w-6 h-6 text-[#0E8DB5] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-serif font-bold text-earth mb-3">{post.title}</h3>
                <p className="text-sm font-sans text-earth-muted leading-relaxed mb-4">{post.excerpt}</p>
                <p className="text-xs font-sans font-semibold text-[#0E8DB5] tracking-wider uppercase">By J.R. — Peer Support Specialist</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Message */}
      <section className="py-16 bg-bone text-center">
        <div className="max-w-3xl mx-auto px-6">
          <p className="animate-on-scroll text-2xl sm:text-3xl font-serif font-bold tracking-tight text-earth leading-snug">
            "Your feelings matter. Help is always within reach.<br /><span className="gradient-text">You are not alone."</span>
          </p>
          <p className="animate-on-scroll delay-100 text-sm font-sans text-earth-muted mt-4">— J.R., Peer Support Specialist, TYSS</p>
        </div>
      </section>
    </main>
  );
}
